package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.courses.CourseAssignmentDto
import com.evoapps.evoshift.data.dto.courses.CourseCompletionDto
import com.evoapps.evoshift.data.dto.courses.CourseDto
import com.evoapps.evoshift.data.dto.courses.CourseKeyDto
import com.evoapps.evoshift.data.dto.courses.CoursePageDto
import com.evoapps.evoshift.data.dto.courses.CoursePresentationDto
import com.evoapps.evoshift.data.dto.courses.CourseProgressDto
import com.evoapps.evoshift.data.dto.courses.OhsCourseInfoDto
import com.evoapps.evoshift.data.dto.courses.OhsRecordDto
import com.evoapps.evoshift.data.dto.courses.QuizQuestionDto
import com.evoapps.evoshift.data.dto.courses.TrainingStatusDto
import com.evoapps.evoshift.domain.model.Course
import com.evoapps.evoshift.domain.model.CourseAssignment
import com.evoapps.evoshift.domain.model.CourseCompletion
import com.evoapps.evoshift.domain.model.CourseKey
import com.evoapps.evoshift.domain.model.CourseKind
import com.evoapps.evoshift.domain.model.CoursePage
import com.evoapps.evoshift.domain.model.CoursePageKind
import com.evoapps.evoshift.domain.model.CourseProgress
import com.evoapps.evoshift.domain.model.CoursePresentation
import com.evoapps.evoshift.domain.model.CourseRules
import com.evoapps.evoshift.domain.model.PresentationLinks
import com.evoapps.evoshift.domain.model.PresentationSource
import com.evoapps.evoshift.domain.model.RichText
import com.evoapps.evoshift.domain.model.CourseStatus
import com.evoapps.evoshift.domain.model.OhsCourseInfo
import com.evoapps.evoshift.domain.model.OhsGeneralMode
import com.evoapps.evoshift.domain.model.OhsGeneralPart
import com.evoapps.evoshift.domain.model.OhsPeriodicMode
import com.evoapps.evoshift.domain.model.OhsPeriodicPart
import com.evoapps.evoshift.domain.model.OhsRecord
import com.evoapps.evoshift.domain.model.OhsStationPart
import com.evoapps.evoshift.domain.model.OhsWorkerGroup
import com.evoapps.evoshift.domain.model.QuizQuestion
import com.evoapps.evoshift.domain.model.TrainingState
import com.evoapps.evoshift.domain.model.TrainingStatus
import kotlinx.datetime.LocalDate
import kotlin.time.Instant

/** SH-126 courses. Unknown kinds read as the plainest value, unreadable days and times as missing. */
object CourseMapper {
    private fun date(value: String?): LocalDate? = value?.let { runCatching { LocalDate.parse(it.take(10)) }.getOrNull() }
    private fun instant(value: String?): Instant? = value?.let { runCatching { Instant.parse(it) }.getOrNull() }
    private inline fun <reified E : Enum<E>> enumOr(value: String?, fallback: E): E =
        enumValues<E>().firstOrNull { it.name == value } ?: fallback

    /**
     * SH-211: a page with its formatted lesson (null when there is none, it is unreadable or of a
     * newer format: then [CoursePage.body] is shown) and its presentation. Unsafe presentation
     * addresses (not https) are dropped, so a reader never frames or opens one.
     */
    fun page(dto: CoursePageDto) = CoursePage(
        id = dto.id,
        kind = enumOr(dto.kind, CoursePageKind.TEXT),
        title = dto.title,
        body = dto.body,
        imagePath = dto.imagePath,
        url = dto.url,
        rich = RichText.decode(dto.rich),
        presentation = dto.presentation?.let { p ->
            CoursePresentation(
                source = enumOr(p.source, PresentationSource.UPLOAD),
                link = p.link?.takeIf { CourseRules.isHttpsUrl(it) },
                embedUrl = p.embedUrl?.takeIf { url ->
                    CourseRules.isHttpsUrl(url) && url.substring(8).substringBefore('/').lowercase() in PresentationLinks.EMBED_HOSTS
                },
                filePath = p.filePath,
                fileName = p.fileName,
                fileType = p.fileType,
                pdfPath = p.pdfPath,
                pdfName = p.pdfName
            )
        }
    )

    fun pageDto(page: CoursePage) = CoursePageDto(
        id = page.id,
        kind = page.kind.name,
        title = page.title,
        body = page.body,
        imagePath = page.imagePath,
        url = page.url,
        rich = page.rich?.let { RichText.encode(it) },
        presentation = page.presentation?.let { p ->
            CoursePresentationDto(p.source.name, p.link, p.embedUrl, p.filePath, p.fileName, p.fileType, p.pdfPath, p.pdfName)
        }
    )

    fun toDomain(dto: CourseDto, id: String, workplaceId: String) = Course(
        id = id,
        workplaceId = workplaceId,
        title = dto.title,
        description = dto.description,
        kind = enumOr(dto.kind, CourseKind.GENERAL),
        pages = dto.pages.map { page(it) },
        questions = dto.questions.map { QuizQuestion(it.id, it.text, it.options, it.multi) },
        passPercent = dto.passPercent,
        roles = dto.roles,
        allNewHires = dto.allNewHires,
        dueDays = dto.dueDays,
        countsForReadiness = dto.countsForReadiness,
        ohs = dto.ohs?.let { OhsCourseInfo(it.lessonHours, it.instructorName, it.consultation) },
        version = dto.version,
        status = enumOr(dto.status, CourseStatus.DRAFT),
        publishedAt = instant(dto.publishedAt),
        updatedAt = instant(dto.updatedAt),
        updatedBy = dto.updatedBy
    )

    fun toDto(course: Course) = CourseDto(
        id = course.id,
        workplaceId = course.workplaceId,
        title = course.title,
        description = course.description,
        kind = course.kind.name,
        pages = course.pages.map { pageDto(it) },
        questions = course.questions.map { QuizQuestionDto(it.id, it.text, it.options, it.multi) },
        passPercent = course.passPercent,
        roles = course.roles,
        allNewHires = course.allNewHires,
        dueDays = course.dueDays,
        countsForReadiness = course.countsForReadiness,
        ohs = course.ohs?.let { OhsCourseInfoDto(it.lessonHours, it.instructorName, it.consultation) },
        version = course.version,
        status = course.status.name,
        publishedAt = course.publishedAt?.toString(),
        updatedAt = course.updatedAt?.toString(),
        updatedBy = course.updatedBy
    )

    fun toDomain(dto: CourseKeyDto, courseId: String) = CourseKey(courseId, dto.version, dto.correct)

    fun toDto(key: CourseKey, editorId: String?, at: Instant?) = CourseKeyDto(
        courseId = key.courseId,
        version = key.version,
        correct = key.correct,
        updatedAt = at?.toString(),
        updatedBy = editorId
    )

    fun toDomain(dto: CourseAssignmentDto, courseId: String, userId: String) = CourseAssignment(
        courseId = courseId,
        userId = userId,
        dueOn = date(dto.dueOn),
        assignedAt = instant(dto.assignedAt),
        assignedBy = dto.assignedBy.takeIf { it.isNotEmpty() }
    )

    fun toDto(assignment: CourseAssignment) = CourseAssignmentDto(
        userId = assignment.userId,
        courseId = assignment.courseId,
        dueOn = assignment.dueOn?.toString(),
        assignedAt = (assignment.assignedAt ?: error("An assignment needs its time")).toString(),
        assignedBy = assignment.assignedBy ?: error("An assignment needs who assigned it")
    )

    fun toDomain(dto: CourseProgressDto) = CourseProgress(
        courseId = dto.courseId,
        userId = dto.userId,
        pagesSeen = dto.pagesSeen,
        startedAt = instant(dto.startedAt),
        updatedAt = instant(dto.updatedAt)
    )

    fun toDto(progress: CourseProgress, workplaceId: String) = CourseProgressDto(
        userId = progress.userId,
        courseId = progress.courseId,
        workplaceId = workplaceId,
        pagesSeen = progress.pagesSeen,
        startedAt = progress.startedAt?.toString(),
        updatedAt = progress.updatedAt?.toString()
    )

    fun toDomain(dto: CourseCompletionDto, courseId: String, userId: String) = CourseCompletion(
        courseId = courseId,
        userId = userId,
        version = dto.version,
        score = dto.score,
        passed = dto.passed,
        attempts = dto.attempts,
        attemptsToday = dto.attemptsToday,
        attemptDay = date(dto.attemptDay),
        completedAt = instant(dto.completedAt),
        lastAttemptAt = instant(dto.lastAttemptAt),
        displayName = dto.displayName
    )

    fun toDomain(dto: OhsRecordDto, userId: String, workplaceId: String) = OhsRecord(
        userId = userId,
        workplaceId = workplaceId,
        displayName = dto.displayName,
        group = dto.group?.let { g -> OhsWorkerGroup.entries.firstOrNull { it.name == g } },
        stationRequired = dto.stationRequired,
        general = dto.general?.let { g ->
            date(g.completedOn)?.let { completedOn ->
                OhsGeneralPart(
                    mode = enumOr(g.mode, OhsGeneralMode.IN_PERSON),
                    conductedBy = g.conductedBy.orEmpty(),
                    completedOn = completedOn,
                    courseId = g.courseId,
                    courseVersion = g.courseVersion,
                    courseTitle = g.courseTitle,
                    score = g.score,
                    employeeConfirmedAt = instant(g.employeeConfirmedAt),
                    recordedByName = g.recordedByName
                )
            }
        },
        station = dto.station?.let { s ->
            val conductedOn = date(s.conductedOn) ?: return@let null
            val admittedOn = date(s.admittedOn) ?: return@let null
            OhsStationPart(s.position, s.conductedBy, conductedOn, admittedOn, s.recordedByName, instant(s.recordedAt), instant(s.employeeConfirmedAt))
        },
        periodic = dto.periodic?.let { p ->
            OhsPeriodicPart(
                mode = enumOr(p.mode, OhsPeriodicMode.SELF_STUDY),
                courseId = p.courseId,
                courseVersion = p.courseVersion,
                courseTitle = p.courseTitle,
                score = p.score,
                testPassedOn = date(p.testPassedOn),
                examOn = date(p.examOn),
                commission = p.commission,
                certificateNumber = p.certificateNumber,
                organizer = p.organizer,
                recordedByName = p.recordedByName
            )
        },
        updatedAt = instant(dto.updatedAt)
    )

    fun toDomain(dto: TrainingStatusDto, userId: String, workplaceId: String) = TrainingStatus(
        userId = userId,
        workplaceId = workplaceId,
        state = enumOr(dto.state, TrainingState.PENDING),
        done = dto.done,
        pending = dto.pending,
        dueOn = date(dto.dueOn)
    )
}
